import { RemoveButton } from "@/components/RemoveButton";
import { TableActions } from "@/components/TableWithSelect/components/TableActions";
import { Button } from "@/components/button";

import Skeleton from "react-loading-skeleton";

import { twMerge } from "tailwind-merge";

type EntryType = { id: string };

type EntryRenderProps = {
  label: string;
  labelBottom?: string;
};

type ListFormProps<T extends EntryType> = {
  title: string;
  buttonTitle: string;
  data: T[];
  onAdd: () => void;
  onEdit?: (entry: T) => void;
  onDelete: (id: string) => void;
  entryRender: (entry: T) => EntryRenderProps;
  className?: string;
  disabled?: boolean;
  loadingData?: boolean;
  emptyMessage?: string;
  addButtonDisabled?: boolean;
};

export function ListForm<T extends EntryType>({
  title,
  buttonTitle,
  data = [],
  onAdd,
  onEdit,
  onDelete,
  entryRender,
  className,
  disabled,
  loadingData,
  emptyMessage,
  addButtonDisabled,
}: ListFormProps<T>) {
  return (
    <div className={twMerge("divide-y text-sm md:border", className)}>
      <div className="flex items-center justify-between px-3 py-4 md:px-6">
        <h3 className="font-dm-sans font-medium">{title}</h3>
        <Button
          label={buttonTitle}
          color="secondary"
          onClick={onAdd}
          disabled={disabled || addButtonDisabled}
          className="md:min-w-[200px]"
        />
      </div>
      <Entries
        data={data}
        onEdit={onEdit}
        onDelete={onDelete}
        entryRender={entryRender}
        disabled={disabled}
        loadingData={loadingData}
        emptyMessage={emptyMessage}
      />
    </div>
  );
}

type EntriesProps<T extends EntryType> = {
  data: T[];
  onEdit?: (entry: T) => void;
  onDelete: (id: string) => void;
  entryRender: (entry: T) => EntryRenderProps;
  disabled?: boolean;
  loadingData?: boolean;
  emptyMessage?: string;
};

const Entries = <T extends EntryType>({
  data,
  onEdit,
  onDelete,
  entryRender,
  disabled,
  loadingData,
  emptyMessage,
}: EntriesProps<T>) => {
  if (loadingData) {
    return <Skeleton count={3} height={48} />;
  }

  if (data.length === 0) {
    return <div className="bg-grey1 p-6 text-center">{emptyMessage}</div>;
  }

  return data.map((entry) => (
    <Entry
      key={entry.id}
      entry={entry}
      {...entryRender(entry)}
      onEdit={onEdit}
      onDelete={onDelete}
      disabled={disabled}
    />
  ));
};

type EntryProps<T extends EntryType> = EntryRenderProps & {
  entry: T;
  onEdit?: (entry: T) => void;
  onDelete: (id: string) => void;
  disabled?: boolean;
};

const Entry = <T extends EntryType>({
  entry,
  label,
  labelBottom,
  onEdit,
  onDelete,
  disabled,
}: EntryProps<T>) => (
  <div className="flex items-center justify-between px-3 py-4 md:px-6">
    <div>
      <div className={twMerge(labelBottom && "font-medium")}>{label}</div>
      {labelBottom && <div>{labelBottom}</div>}
    </div>
    {onEdit ? (
      <TableActions
        className="mr-0"
        onEdit={() => onEdit(entry)}
        onDelete={() => onDelete(entry.id)}
        row={entry}
        editDisabled={disabled}
        deleteDisabled={disabled}
      />
    ) : (
      <RemoveButton
        onClick={() => onDelete(entry.id)}
        type="button"
        disabled={disabled}
      />
    )}
  </div>
);
